import {
    useRef,
    useState
} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {useVerifiedPermissions} from '@flexinet/auth-components';

import {useProductLineItems} from '../hooks/useProductLineItems.ts';
import {useProductLineItem} from '../hooks/useProductLineItem.ts';
import {useProductLineItemsSelection} from '../hooks/useProductLineItemsSelection.ts';
import {useProductLineItemsTableColumns} from '../hooks/useProductLineItemsTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {rbacActions} from '../../../config/rbac/actions.ts';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {ProductLineItem} from '../types';

interface RecordsTableProps {
    productId: string;
}

export const RecordsTable = ({productId}: RecordsTableProps) => {
    const {selectedRecords, setSelectedRecords} = useProductLineItemsSelection();
    const {columns} = useProductLineItemsTableColumns();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<ProductLineItem | null>(null);

    // Editing is a write, so the row click that opens the edit drawer is gated on
    // UpdateProductLineItem, as Add and Delete are on theirs.
    const {allowedActions} = useVerifiedPermissions();
    const canEdit = allowedActions.includes(rbacActions.UpdateProductLineItem);

    const {
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useProductLineItems(productId);

    // The edit goes through the mutation hook: whitelisted input, a toast either way,
    // invalidation of the list. Resolves true when saved; the drawer closes only then.
    const {updateRecord} = useProductLineItem(productId);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const term = keyword.trim();

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load product line items')}>
                    {I18n.get('The billing API did not return the line items for this product. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<ProductLineItem>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={canEdit ? ({record}) => {
                    setSelectedRecord(record);
                    setDrawerOpened(true);
                } : undefined}
            />

            {/*
              EMPTY-ISH STATES (§13). A search with no match, and genuinely nothing yet. There
              is no filter drawer, so no third "filter matched nothing" state. Gated on
              !loading so it does not flash during the first fetch, and on !isError so a
              failed load keeps its own Alert rather than claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                term ? (
                    <Alert color='gray' title={I18n.get('No matching line items')}>
                        {`${I18n.get('Nothing in the loaded line items matches')} "${term}". ${I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded yet.')}`}
                    </Alert>
                ) : (
                    <Alert color='blue' title={I18n.get('No line items yet')}>
                        {I18n.get('A line item adds a product feature to this product. Use Add Product Line Item to add the first one.')}
                    </Alert>
                )
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'line item'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={updateRecord}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
